import { format } from 'date-fns';
import {
  faStar,
  faPlay,
  faFileCircleCheck,
  faComment,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { PATHS } from '@client/routes';
import { TMap } from '@client/new/helpers/types';
import {
  cellIconText,
  CellProps,
  MenuItems,
} from '@client/new/components/TableShared/Cell';
import {
  StandardTableProps,
  Row,
} from '@client/new/components/StandardTable/StandardTable';
import { LiteWorkout } from '@client/new/pages/collection/workouts/workouts.domain';
import { workoutTypeToTab } from '@client/new/pages/collection/domain';
import { Tag } from '@design-system/components/tag/tag';
import {
  commentsLink,
  commentsLinkLabel,
  projectTag,
  projectTagLabel,
} from './workoutTableExtras.css';

export type WorkoutsSearchVM = {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
};

// Client-side title filter, matching workouts.view.tsx (case-insensitive
// substring on the workout's title).
export const filterWorkoutsByTitle = (
  workouts: LiteWorkout[],
  searchText: string,
): LiteWorkout[] =>
  workouts.filter((w) =>
    w.orgTitle.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()),
  );

export const toProgramNameById = (
  workouts: Array<{ id: number; programName?: string | null }>,
): TMap<number, string> =>
  workouts.reduce(
    (acc, w) => ({ ...acc, [w.id]: w.programName ?? '' }),
    {} as TMap<number, string>,
  );

type ListKind = 'draft' | 'scheduled' | 'archived';

type WorkoutRowActions = {
  onDuplicate: (workoutId: number) => void;
  onArchive?: (workoutId: number) => void;
  onDelete?: (workoutId: number) => void;
};

type BuildWorkoutsListTableParams = {
  kind: ListKind;
  influencerId: number;
  workouts: LiteWorkout[];
  programNameById: TMap<number, string>;
  actions: WorkoutRowActions;
};

export const getFormattedDate = (date: string) =>
  format(new Date(date), 'MMM d yyyy, h:mma');

const nameColumn = { width: 'stretchable', label: 'Workout name' } as const;
const programColumn = {
  width: 250,
  label: 'Program name',
  justify: 'center',
} as const;
const actionColumn = { width: 50, label: '', justify: 'right' } as const;
export const freeSampleColumn = { width: 110, label: 'Free sample' } as const;
const statColumns = [
  { width: 100, label: 'rating' },
  { width: 100, label: 'begins' },
  { width: 100, label: 'completions' },
  { width: 100, label: 'comments' },
] as const;

const schemaFor = (kind: ListKind): StandardTableProps<number>['schema'] =>
  kind === 'archived'
    ? [
        nameColumn,
        programColumn,
        freeSampleColumn,
        ...statColumns,
        actionColumn,
      ]
    : kind === 'draft'
    ? [nameColumn, programColumn, freeSampleColumn, actionColumn]
    : [nameColumn, programColumn, actionColumn];

const projectNameCell = (name: string): CellProps => ({
  type: 'Custom',
  value: name ? (
    <Tag
      readonly
      label={name}
      className={projectTag}
      labelClassName={projectTagLabel}
    />
  ) : (
    <></>
  ),
});

// A workout is a free sample when it is not protected behind the paywall.
export const freeSampleCell = (w: LiteWorkout): CellProps =>
  w.freeSample ? cellIconText(undefined, faCheck) : cellIconText('');

export const statsCells = (
  w: LiteWorkout,
  influencerId: number,
  programName?: string,
): CellProps[] => [
  cellIconText(w.rating || '-', faStar),
  cellIconText(w.begins, faPlay),
  cellIconText(w.completions, faFileCircleCheck),
  {
    type: 'Link',
    value: {
      to: `/admin/${influencerId}/workout/${w.workoutId}/comments${
        programName ? `?programName=${encodeURIComponent(programName)}` : ''
      }`,
      label: w.comments,
      icon: faComment,
      classNames: {
        classNameWrapper: commentsLink,
        classNameLabel: commentsLinkLabel,
      },
    },
  },
];

const subtitleFor = (kind: ListKind, w: LiteWorkout): string => {
  const date =
    kind === 'draft'
      ? w.lastEditedDate
      : kind === 'scheduled'
      ? w.scheduledDate
      : w.archivedDate;
  if (!date) return '';
  const prefix =
    kind === 'draft'
      ? 'Last edited '
      : kind === 'scheduled'
      ? 'Scheduled for '
      : 'Archived ';
  return prefix + getFormattedDate(date);
};

const menuItemsFor = (
  kind: ListKind,
  influencerId: number,
  actions: WorkoutRowActions,
  w: LiteWorkout,
): MenuItems => {
  const items: MenuItems = [
    {
      text: 'Edit',
      to: PATHS.WORKOUT_EDIT({
        workoutId: w.workoutId,
        influencerId,
        programId: w.programId,
        fromWorkoutTab: workoutTypeToTab[kind],
      }),
    },
    { text: 'Duplicate', onClick: () => actions.onDuplicate(w.workoutId) },
  ];

  if (kind === 'draft' && actions.onDelete)
    items.push({
      text: 'Delete',
      onClick: () => actions.onDelete?.(w.workoutId),
    });

  if (kind === 'scheduled' && actions.onArchive)
    items.push({
      text: 'Archive',
      onClick: () => actions.onArchive?.(w.workoutId),
    });

  return items;
};

export const buildWorkoutsListTable = ({
  kind,
  influencerId,
  workouts,
  programNameById,
  actions,
}: BuildWorkoutsListTableParams): StandardTableProps<number> => {
  const rows: Array<Row<number>> = workouts.map((w) => {
    const head: CellProps = {
      type: 'Head',
      value: { title: w.orgTitle, subtitle: subtitleFor(kind, w) },
    };
    const project = projectNameCell(programNameById[w.workoutId] ?? '');
    const menu: CellProps = {
      type: 'Menu',
      value: { items: menuItemsFor(kind, influencerId, actions, w) },
    };

    const cells: CellProps[] =
      kind === 'archived'
        ? [
            head,
            project,
            freeSampleCell(w),
            ...statsCells(w, influencerId),
            menu,
          ]
        : kind === 'draft'
        ? [head, project, freeSampleCell(w), menu]
        : [head, project, menu];

    return { id: w.workoutId, cells };
  });

  return { schema: schemaFor(kind), rows };
};
